import React, { useState } from 'react';
import { Box, Typography, Button, Dialog, TextField, Radio, RadioGroup, FormControlLabel, IconButton, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';
import { marketingService, CreateBroadcastRequest } from '../../services/marketing.service';

interface CreateBroadcastDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateBroadcastDialog: React.FC<CreateBroadcastDialogProps> = ({ open, onClose }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendOption, setSendOption] = useState('schedule');
  const [sendDate, setSendDate] = useState('');
  const [sendHour, setSendHour] = useState('12');
  const [sendMinute, setSendMinute] = useState('00');
  const [sendPeriod, setSendPeriod] = useState<'AM' | 'PM'>('PM');
  const [loading, setLoading] = useState(false);

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or valid numbers 1-12
    if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 12)) {
      setSendHour(value);
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or valid numbers 0-59
    if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 59)) {
      setSendMinute(value);
    }
  };

  const handleCreate = async () => {
    if (!isFormValid) return;
    
    try {
      setLoading(true);
      
      let scheduledAt: string;
      if (sendOption === 'now') {
        scheduledAt = new Date().toISOString();
      } else {
        const dateValue = new Date(sendDate);
        let hour = parseInt(sendHour, 10) || 12;
        
        // Convert 12-hour to 24-hour format
        if (sendPeriod === 'AM') {
          if (hour === 12) hour = 0; // 12 AM = 0 hours
        } else {
          if (hour !== 12) hour += 12; // PM hours except 12 PM
        }
        
        dateValue.setHours(hour);
        dateValue.setMinutes(parseInt(sendMinute, 10) || 0);
        dateValue.setSeconds(0);
        scheduledAt = dateValue.toISOString();
      }
      
      const request: CreateBroadcastRequest = {
        title,
        message,
        scheduledAt,
        type: 'MESSAGE',
        targetAudience: 'ALL_USERS'
      };
      
      await marketingService.createBroadcast(request);
      toast.success('Broadcast created successfully');
      
      setTitle('');
      setMessage('');
      setSendDate('');
      setSendHour('12');
      setSendMinute('00');
      setSendPeriod('PM');
      
      onClose();
    } catch (error) {
      console.error('Failed to create broadcast:', error);
      toast.error('Failed to create broadcast');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = title && message && (sendOption === 'now' || (sendDate && sendHour && sendMinute));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 'var(--palette-light-3-value)',
          boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
          maxWidth: '508px',
          width: '100%',
          m: 'var(--palette-light-6-value)',
        },
      }}
    >
      <Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h3-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
            New broadcast message
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              width: 'var(--palette-light-8-value)',
              height: 'var(--palette-light-8-value)',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.8,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
          {/* Title */}
          <TextField
            placeholder="Placeholder"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--palette-light-4-value)',
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  padding: '6px 12px',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  '&::placeholder': {
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: 'var(--palette-light-font-size-default-value)',
                color: 'var(--palette-light-text-secondary-value-hex)',
                transform: 'translate(12px, 6px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(12px, -9px) scale(0.75)',
                },
              },
            }}
            label="Title"
            InputLabelProps={{ shrink: true }}
          />

          {/* Message */}
          <TextField
            placeholder="Placeholder"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--palette-light-4-value)',
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                '& fieldset': {
                  border: 'none',
                },
                '& textarea': {
                  padding: '6px 12px',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  '&::placeholder': {
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: 'var(--palette-light-font-size-default-value)',
                color: 'var(--palette-light-text-secondary-value-hex)',
                transform: 'translate(12px, 6px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(12px, -9px) scale(0.75)',
                },
              },
            }}
            label="Message"
            InputLabelProps={{ shrink: true }}
          />

          {/* Send Option */}
          <RadioGroup value={sendOption} onChange={(e) => setSendOption(e.target.value)} sx={{ display: 'flex', flexDirection: 'row', gap: 'var(--palette-light-8-value)' }}>
            <FormControlLabel
              value="schedule"
              control={
                <Radio
                  sx={{
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    '&.Mui-checked': {
                      color: 'var(--palette-light-brand-primary-value-hex)',
                    },
                  }}
                />
              }
              label="Schedule"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                },
              }}
            />
            <FormControlLabel
              value="now"
              control={
                <Radio
                  sx={{
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    '&.Mui-checked': {
                      color: 'var(--palette-light-brand-primary-value-hex)',
                    },
                  }}
                />
              }
              label="Send now"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                },
              }}
            />
          </RadioGroup>

          {/* Date and Time */}
          <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
            <TextField
              type="date"
              value={sendDate}
              onChange={(e) => setSendDate(e.target.value)}
              fullWidth
              disabled={sendOption === 'now'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--palette-light-4-value)',
                  backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                  '& fieldset': {
                    border: 'none',
                  },
                  '& input': {
                    padding: '6px 12px',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    '&::placeholder': {
                      color: 'var(--palette-light-text-secondary-value-hex)',
                      opacity: 1,
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                  transform: 'translate(12px, 6px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(12px, -9px) scale(0.75)',
                  },
                },
              }}
              label="Send on"
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
              <TextField
                placeholder="12"
                value={sendHour}
                onChange={handleHourChange}
                disabled={sendOption === 'now'}
                inputProps={{ min: 1, max: 12 }}
                sx={{
                  width: '70px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--palette-light-4-value)',
                    backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '6px 8px',
                      fontSize: 'var(--palette-light-font-size-default-value)',
                      textAlign: 'center',
                      '&::placeholder': {
                        color: 'var(--palette-light-text-secondary-value-hex)',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    transform: 'translate(8px, 6px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(8px, -9px) scale(0.75)',
                    },
                  },
                }}
                label="HH"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                placeholder="00"
                value={sendMinute}
                onChange={handleMinuteChange}
                disabled={sendOption === 'now'}
                inputProps={{ min: 0, max: 59 }}
                sx={{
                  width: '70px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--palette-light-4-value)',
                    backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '6px 8px',
                      fontSize: 'var(--palette-light-font-size-default-value)',
                      textAlign: 'center',
                      '&::placeholder': {
                        color: 'var(--palette-light-text-secondary-value-hex)',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    transform: 'translate(8px, 6px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(8px, -9px) scale(0.75)',
                    },
                  },
                }}
                label="MM"
                InputLabelProps={{ shrink: true }}
              />
              <Select
                value={sendPeriod}
                onChange={(e) => setSendPeriod(e.target.value as 'AM' | 'PM')}
                disabled={sendOption === 'now'}
                sx={{
                  width: '80px',
                  borderRadius: 'var(--palette-light-4-value)',
                  backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '6px 8px',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    textAlign: 'center',
                  },
                }}
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!isFormValid || loading}
            fullWidth
            sx={{
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: 'var(--palette-light-background-tertiary-value-hex)',
                color: 'var(--palette-light-text-tertiary-value-hex)',
              },
            }}
          >
            {loading ? 'Creating...' : (sendOption === 'schedule' ? 'Add to Schedule' : 'Send')}
          </Button>
          <Button
            variant="text"
            onClick={onClose}
            fullWidth
            sx={{
              color: 'var(--palette-light-brand-primary-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateBroadcastDialog;
